import { z } from 'zod'
import type { CraftShowPersonSummary, ExhibitorHistory } from './source-exhibitors'
import type { SourceOrganizationSummary } from './source-lookup'
import type { SourceOrganizationPage } from './source-organizations'
import { SourceConsentPageSchema, type SourceConsentPage } from './source-consent'

// The contract Craft Show implements (ADR 0021), pinned so a change can't
// break the Craft Show import silently (ADR 0026).
//
// Craft Show can't import this package, so it keeps a hand copy of these
// types (craft-show apps/api/src/lib/hub-contracts.ts) with the same
// version number (HUB_CONTRACT_VERSION there). Three things hold the two
// together:
//
//   1. The schemas below restate, field for field, the three answers Craft
//      Show sends: the organization feed page, the organization summary
//      (with its exhibitor history) and the person summary. A compile-time
//      check makes each schema's type EQUAL to the type in
//      source-organizations.ts, source-lookup.ts and source-exhibitors.ts,
//      so editing a type without its schema fails `pnpm typecheck`.
//   2. fixtures/craft-show-contract.json is generated from these schemas
//      (JSON Schema), with their hash, this version and worked examples.
//      craft-show-contract.test.ts fails when the file is stale, and the
//      generator refuses to rewrite it under the same version when the
//      schemas changed: a change to what Craft Show must send needs
//      CRAFT_SHOW_CONTRACT_VERSION bumped.
//   3. Craft Show checks in a byte-for-byte copy of that file, tests that
//      its HUB_CONTRACT_VERSION matches it, and validates its real answers
//      against the schemas in it.
//
// To change the contract: change the type, its schema here and its
// examples, bump CRAFT_SHOW_CONTRACT_VERSION, run
//   UPDATE_CRAFT_SHOW_CONTRACT=1 pnpm --filter @bre-hub/contracts test craft-show-contract
// then copy fixtures/craft-show-contract.json to Craft Show
// (apps/api/test/fixtures/hub-craft-show-contract.json), update its
// hub-contracts.ts and HUB_CONTRACT_VERSION, and release Craft Show first
// (docs/runbooks/deploy.md, "Craft Show contract").

// Version 2 (2026-10-08, ADR 0034) added the consent feed: Craft Show's
// promotional grants and opt-outs, for Hub's consent ledger.
export const CRAFT_SHOW_CONTRACT_VERSION = 2

const str = z.string()
const strOrNull = z.string().nullable()
const minor = z.number().int().min(0)
const count = z.number().int().min(0)

const SourceOrganizationSchema = z.object({
  sourceId: str,
  name: str,
  kind: strOrNull,
  status: z.enum(['active', 'archived']),
  email: strOrNull,
  phone: strOrNull,
  address: strOrNull,
  website: strOrNull,
  createdAt: strOrNull,
  updatedAt: str,
  url: strOrNull,
})

const SourceOrganizationTombstoneSchema = z.object({ sourceId: str, deletedAt: str, mergedInto: strOrNull, updatedAt: str })

export const CraftShowOrganizationPageSchema = z.object({
  version: z.literal(1),
  asOf: str,
  organizations: z.array(SourceOrganizationSchema),
  tombstones: z.array(SourceOrganizationTombstoneSchema),
  nextCursor: strOrNull,
  hasMore: z.boolean(),
})

const ExhibitorPaymentSchema = z.object({ billedMinor: minor, paidMinor: minor, refundedMinor: minor, outstandingMinor: minor, currency: str, status: str })

const ExhibitorShowSchema = z.object({
  eventId: str,
  eventName: str,
  startsAt: strOrNull,
  applicationId: str,
  applicationStatus: str,
  submittedAt: strOrNull,
  booths: z.array(str),
  assignmentStatus: strOrNull,
  payment: ExhibitorPaymentSchema.nullable(),
  checkIn: z.object({ status: str, at: str }).nullable(),
  url: strOrNull,
})

const ExhibitorDocumentSchema = z.object({ kind: z.enum(['agreement', 'insurance']), name: str, status: str, eventName: strOrNull, date: strOrNull })

const exhibitorHistoryShape = {
  exhibitor: z.object({ sourceId: str, name: str, kind: z.enum(['business', 'individual']).nullable(), status: str }),
  categories: z.array(str),
  documents: z.array(ExhibitorDocumentSchema),
  shows: z.array(ExhibitorShowSchema),
  url: strOrNull,
}
const ExhibitorHistorySchema = z.object(exhibitorHistoryShape)

const OrganizationItemSchema = z.object({
  kind: z.enum(['sponsorship', 'rental', 'invoice', 'group_sale', 'contract', 'other']),
  sourceId: str,
  title: str,
  status: strOrNull,
  date: strOrNull,
  amountMinor: z.number().int().nullable(),
  balanceMinor: z.number().int().nullable(),
  currency: strOrNull,
  url: strOrNull,
})

export const CraftShowOrganizationSummarySchema = z.object({
  version: z.literal(1),
  asOf: str,
  organization: z.object({ sourceId: str, name: str }),
  items: z.array(OrganizationItemSchema),
  itemCount: count,
  billing: z.object({ billedMinor: z.number().int(), paidMinor: z.number().int(), outstandingMinor: z.number().int(), currency: str }).nullable(),
  contacts: z.array(z.object({ sub: strOrNull, name: strOrNull, email: strOrNull, role: strOrNull })),
  exhibitor: ExhibitorHistorySchema.optional(),
})

export const CraftShowPersonSummarySchema = z.object({
  version: z.literal(1),
  asOf: str,
  exhibitors: z.array(z.object({ ...exhibitorHistoryShape, role: z.enum(['primary', 'additional', 'billing', 'event_day']) })),
})

// ------------------------------------------- the schemas ARE the types

// Each assignable to the other: a field added, dropped, renamed or retyped
// on either side fails typecheck.
type Equal<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false
const same = <T extends true>(_: T) => _
same<Equal<z.infer<typeof CraftShowOrganizationPageSchema>, SourceOrganizationPage>>(true)
same<Equal<z.infer<typeof ExhibitorHistorySchema>, ExhibitorHistory>>(true)
same<Equal<z.infer<typeof CraftShowOrganizationSummarySchema>, SourceOrganizationSummary>>(true)
same<Equal<z.infer<typeof CraftShowPersonSummarySchema>, CraftShowPersonSummary>>(true)
same<Equal<z.infer<typeof SourceConsentPageSchema>, SourceConsentPage>>(true)

// --------------------------------------------------- what gets published

export const CRAFT_SHOW_CONTRACT_SCHEMAS = {
  organizationPage: CraftShowOrganizationPageSchema,
  organizationSummary: CraftShowOrganizationSummarySchema,
  personSummary: CraftShowPersonSummarySchema,
  consentPage: SourceConsentPageSchema,
} as const

// Worked examples, one per answer: what a correct Craft Show answer looks
// like. Hub's adapters must read each one back unchanged
// (packages/adapters/src/craft-show-contract.test.ts).
const show: ExhibitorHistory['shows'][number] = {
  eventId: 'evt_2026',
  eventName: '2026 Christmas Craft Show',
  startsAt: '2026-11-27T15:00:00.000Z',
  applicationId: 'app_1',
  applicationStatus: 'fully_confirmed',
  submittedAt: '2026-06-01T14:00:00.000Z',
  booths: ['A12', 'A13'],
  assignmentStatus: 'confirmed',
  payment: { billedMinor: 25000, paidMinor: 25000, refundedMinor: 0, outstandingMinor: 0, currency: 'usd', status: 'paid' },
  checkIn: { status: 'checked_in', at: '2026-11-27T13:05:00.000Z' },
  url: 'https://craftshow.example/admin/applications/app_1',
}
const history: ExhibitorHistory = {
  exhibitor: { sourceId: 'vnd_pottery', name: 'Hub Pottery', kind: 'business', status: 'active' },
  categories: ['Pottery'],
  documents: [{ kind: 'insurance', name: 'Certificate of insurance', status: 'approved', eventName: '2026 Christmas Craft Show', date: '2026-10-01' }],
  shows: [show, { ...show, eventId: 'evt_2025', eventName: '2025 Christmas Craft Show', applicationId: 'app_0', booths: [], assignmentStatus: null, payment: null, checkIn: null, startsAt: null, submittedAt: null, url: null }],
  url: 'https://craftshow.example/admin/applications/app_1',
}

export const CRAFT_SHOW_CONTRACT_EXAMPLES: {
  organizationPage: SourceOrganizationPage
  organizationSummary: SourceOrganizationSummary
  personSummary: CraftShowPersonSummary
  consentPage: SourceConsentPage
} = {
  organizationPage: {
    version: 1,
    asOf: '2026-10-02T12:00:00.000Z',
    organizations: [
      {
        sourceId: 'vnd_pottery',
        name: 'Hub Pottery',
        kind: 'Exhibitor',
        status: 'active',
        email: 'hello@pottery.example',
        phone: null,
        address: null,
        website: 'https://pottery.example',
        createdAt: '2026-05-01T10:00:00.000Z',
        updatedAt: '2026-10-01T10:00:00.000Z',
        url: 'https://craftshow.example/admin/applications/app_1',
      },
    ],
    tombstones: [{ sourceId: 'vnd_gone', deletedAt: '2026-09-01T00:00:00.000Z', mergedInto: null, updatedAt: '2026-09-01T00:00:00.000Z' }],
    nextCursor: 'opaque-cursor',
    hasMore: false,
  },
  organizationSummary: {
    version: 1,
    asOf: '2026-10-02T12:00:00.000Z',
    organization: { sourceId: 'vnd_pottery', name: 'Hub Pottery' },
    items: [{ kind: 'other', sourceId: 'app_1', title: 'Application: 2026 Christmas Craft Show', status: 'fully_confirmed', date: '2026-06-01', amountMinor: 25000, balanceMinor: 0, currency: 'usd', url: null }],
    itemCount: 1,
    billing: { billedMinor: 25000, paidMinor: 25000, outstandingMinor: 0, currency: 'usd' },
    contacts: [{ sub: 'sub-owner', name: 'Pat Potter', email: 'pat@pottery.example', role: 'primary' }],
    exhibitor: history,
  },
  personSummary: {
    version: 1,
    asOf: '2026-10-02T12:00:00.000Z',
    exhibitors: [
      { ...history, role: 'primary' },
      { ...history, exhibitor: { sourceId: 'vnd_jane', name: 'Jane Doe', kind: 'individual', status: 'active' }, documents: [], shows: [], categories: [], url: null, role: 'primary' },
    ],
  },
  consentPage: {
    version: 1,
    asOf: '2026-10-08T12:00:00.000Z',
    items: [
      {
        id: 'crec_1:grant:1780000000000',
        kind: 'grant',
        channel: 'email',
        address: 'pat@pottery.example',
        subject: 'sub-owner',
        scope: 'promotional',
        occurredAt: '2026-06-01T14:00:00.000Z',
        sourceKind: 'application_form',
        evidence: { statement: 'Send me news about the Christmas Craft Show by email.', statementVersion: '1', detail: null },
      },
      {
        id: 'crec_2:withdraw:1790000000000',
        kind: 'withdraw',
        channel: 'sms',
        address: '+13195550100',
        subject: null,
        scope: 'promotional',
        occurredAt: null,
        sourceKind: 'sms_keyword',
        evidence: { statement: null, statementVersion: null, detail: null },
      },
    ],
    nextCursor: 'opaque-cursor',
    hasMore: false,
  },
}

// Key order made canonical, so the hash is the shape and nothing else.
const canonical = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(canonical)
    : value && typeof value === 'object'
      ? Object.fromEntries(Object.keys(value as Record<string, unknown>).sort().map((k) => [k, canonical((value as Record<string, unknown>)[k])]))
      : value

export function craftShowContractJsonSchemas(): Record<keyof typeof CRAFT_SHOW_CONTRACT_SCHEMAS, unknown> {
  return {
    organizationPage: canonical(z.toJSONSchema(CraftShowOrganizationPageSchema)),
    organizationSummary: canonical(z.toJSONSchema(CraftShowOrganizationSummarySchema)),
    personSummary: canonical(z.toJSONSchema(CraftShowPersonSummarySchema)),
    consentPage: canonical(z.toJSONSchema(SourceConsentPageSchema)),
  }
}

export async function craftShowContractHash(): Promise<string> {
  const bytes = new TextEncoder().encode(JSON.stringify(craftShowContractJsonSchemas()))
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export type CraftShowContractFixture = {
  contract: 'bre-hub/craft-show'
  version: number
  schemaHash: string
  // Every version published so far, oldest first: a version is never
  // reused for a different shape.
  history: { version: number; schemaHash: string }[]
  note: string
  schemas: Record<keyof typeof CRAFT_SHOW_CONTRACT_SCHEMAS, unknown>
  examples: typeof CRAFT_SHOW_CONTRACT_EXAMPLES
}
